import { useState, useMemo } from "react";
import { SecondaryDeviceFullDto } from "../api/types.gen";


type CameraEsque = Pick<SecondaryDeviceFullDto, "id" | "name" | "vendor" | "model" | "externalId" | "monitored">;

export type UseDeviceFilterToggleInput<T extends CameraEsque> = {
  cameras: T[]
  onToggle: (ids: string[], enabled: boolean) => void
}

/** Used for filtering devices and toggling those results */
export const useDeviceFilterToggle = <T extends CameraEsque>({ cameras, onToggle }: UseDeviceFilterToggleInput<T>) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCameras = useMemo(() => {
    if (!searchQuery) return cameras;

    const query = searchQuery.toLowerCase();
    return cameras.filter(
      (camera) =>
        camera.name?.toLowerCase().includes(query) ||
        camera.vendor?.toLowerCase().includes(query) ||
        camera.model?.toLowerCase().includes(query) ||
        camera.externalId?.toLowerCase().includes(query)
    );
  }, [cameras, searchQuery]);
  const isFiltering = filteredCameras.length !== cameras.length;
  const selectMode = filteredCameras.some((x) => !x.monitored) ? 'select' : 'deselect';

  const onClear = () => {
    setSearchQuery('');
  };

  const onToggleFilteredCameras = () => {
    const ids = filteredCameras.map((x) => x.id).filter((x) => x != null);

    switch (selectMode) {
      case 'select': {
        onToggle(ids, true);
        break;
      }
      case 'deselect': {
        onToggle(ids, false);
        break;
      }
    }
  };

  const onToggleAll = () => {
    const ids = cameras.map((x) => x.id).filter((x) => x != null);

    switch (selectMode) {
      case 'select': {
        onToggle(ids, true);
        break;
      }
      case 'deselect': {
        onToggle(ids, false);
        break;
      }
    }
  }

  return {
    searchQuery,
    setSearchQuery,
    filteredCameras,
    isFiltering,
    selectMode,
    onClear,
    onToggleFilteredCameras,
    onToggleAll
  }
}